import { zodResolver } from "@hookform/resolvers/zod";
import useToast from "@repo/core/components/core/Toast";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { normalizeProposalIdParam } from "@repo/core/hooks/useRefetchProposalViewInfoQueries/invalidateProposalViewInfoQueries";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { apiInstance } from "@repo/core/services/instances/api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import z from "zod";
import { buildCreditTableObservation } from "./utils";

const formSchema = z.object({
  reason: z.string({ message: "Selecione um motivo" }).min(1),
  observations: z.string().max(100).optional()
});

export type FormDataType = z.infer<typeof formSchema>;

const proposalsResource = new ProposalsResource(apiInstance);

export const SendToCreditTableController = () => {
  const { back, replace } = useNavigation();
  const routeParams = useRouteParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isTablet, isWeb, isMobile } = useDimensions();

  const proposalId = normalizeProposalIdParam(routeParams?.proposalId);
  const isTabletOrWeb = isTablet || isWeb;

  const {
    control,
    formState: { isValid, isSubmitting },
    handleSubmit
  } = useForm<FormDataType>({
    resolver: zodResolver(formSchema),
    mode: "onChange"
  });

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    if (!proposalId) {
      toast.error(
        "Erro ao enviar para mesa",
        "Não foi possível identificar a proposta."
      );
      return;
    }

    try {
      await proposalsResource.sendToManualAnalysis({
        lngProposta: proposalId,
        strObservacao: buildCreditTableObservation(
          formData.reason,
          formData.observations
        )
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["proposalsSearch"] }),
        queryClient.invalidateQueries({
          queryKey: ["proposalDetails", proposalId]
        }),
        queryClient.invalidateQueries({
          queryKey: ["proposalSituation", proposalId]
        }),
        queryClient.invalidateQueries({
          queryKey: ["proposalDocuments", proposalId]
        })
      ]);

      replace(ROUTES.proposalsViewInfo, {
        proposalId
      });
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        "Não foi possível enviar a proposta para a mesa de crédito. Tente novamente.";

      toast.error("Erro ao enviar para mesa", message);
    }
  });

  return {
    control,
    isValid,
    isMobile,
    isSubmitting,
    handleOnSubmitForm,
    back,
    isTabletOrWeb
  };
};
